import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { PageContent } from '../components/ui/PageContent';
import { Footer } from '../components/shell/Footer';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { CountryPicker } from '../components/ui/CountryPicker';
import { AvatarPicker } from '../components/ui/AvatarPicker';
import { ToggleSwitch } from '../components/ui/ToggleSwitch';
import { VolumeSlider } from '../components/ui/VolumeSlider';
import { CosmeticCatalog } from './lobby/catalogComponents';
import { useLogout } from '../hooks/useLogout';
import { useDeleteAccount } from '../hooks/useDeleteAccount';
import { useConfirm } from '../context/ConfirmContext';
import { apiClient } from '../network/ApiClient';
import type { User } from '../types/game';
import { normalizeCountryCode } from '../config/countryCatalog';
import { normalizeAvatarKey, isValidAvatarKey } from '../config/avatars';
import {
  TABLE_SKIN_CATALOG, TILE_SKIN_CATALOG,
  DEFAULT_TABLE_SKIN, DEFAULT_TILE_SKIN,
  normalizeTableSkin, normalizeTileSkin,
  type ViewerTableSkin, type ViewerTileSkin,
} from '../config/viewerCosmetics';
import { PreferenceSettings } from '../config/preferences';
import { AppSoundManager } from '../audio/AppSoundManager';
import { clearRegistrationStatusCache } from '../hooks/useRegistrationStatus';
import { refreshIdentity } from '../hooks/useIdentity';
import { useTranslation } from '../i18n/react';
import { LanguageSelector } from '../components/ui/LanguageSelector';
import { requestPushPermission, getPushPermissionState, unregisterPushDevice, beginPushOperation, setPushEnabledPreference, type PushPermissionState } from '../utils/push';
import { uiKey, renderUiMessage, type UiMessage } from '../i18n/uiMessage';
import { tableSkinDisplayName, tileSkinDisplayName } from '../i18n/cosmetics';

/**
 * SETTINGS — the unified Profile/Settings experience (Figma "Settings"
 * frame, 2560×4112 export, translated onto the DS2 shell + fluid tokens).
 * Canonical at /settings; /profile redirects here. One source component,
 * one source of behavior — the legacy ProfileScreen/SettingsScreen pair
 * was retired with this screen.
 *
 * Persistence model:
 *   • username / avatar / country / table skin / tile skin → backend
 *     (confirmed-save semantics: UI updates only after the server confirms;
 *     a failed save keeps the prior confirmed value and shows role=alert).
 *   • sound volume + table chat → device-local via PreferenceSettings (D2).
 *   • Music is COMING SOON — no music system exists; the control is
 *     disabled and persists nothing (D4).
 *   • Skin selections are account DEFAULTS seeding future sessions
 *     server-side — changing them never mutates an active/waiting session
 *     and never un-readies a lobby (D5).
 */

// ── StrictMode-safe /me single-flight ───────────────────────────────────────
// StrictMode's mount→unmount→remount would fire the initial GET /me twice
// (each mount is a fresh component instance, so refs don't survive). A
// module-level in-flight promise makes both mounts share ONE request; it
// clears on settle so a later visit (or account switch) refetches fresh.
let meInflight: Promise<User> | null = null;
function fetchMeShared(): Promise<User> {
  meInflight ??= apiClient.getMe().finally(() => { meInflight = null; });
  return meInflight;
}

// ── Local DS2 atoms (same precedent as LobbyScreen's section label) ─────────

const CARD_DIVIDER = 'rgba(201,168,76,0.45)';

function FieldLabel({ children, htmlFor, style }: { children: ReactNode; htmlFor?: string; style?: CSSProperties }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display:       'block',
        fontFamily:    DS2.font.heading,
        fontWeight:    700,
        fontSize:      'clamp(0.85rem, 1vw, 16.5px)',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color:         DS2.color.gold,
        ...style,
      }}
    >
      {children}
    </label>
  );
}

function Card({ children, isMobile }: { children: ReactNode; isMobile: boolean }) {
  return (
    <section
      style={{
        background:   '#131313',
        border:       `1px solid ${DS2.color.gold}`,
        borderRadius: DS2.radius.card,
        padding:      isMobile ? '24px 20px' : '30px 44px 38px',
        marginTop:    isMobile ? 24 : 40,
      }}
    >
      {children}
    </section>
  );
}

function CardHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <>
      <h2 style={{ fontFamily: DS2.font.body, fontWeight: 600, fontSize: 'clamp(1.35rem, 1.6vw, 27px)', color: DS2.color.textBright, margin: 0 }}>
        {title}
      </h2>
      <p style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: 'clamp(0.95rem, 1.35vw, 23px)', color: DS2.color.textBright, margin: '6px 0 0' }}>
        {subtitle}
      </p>
    </>
  );
}

function Divider({ style }: { style?: CSSProperties }) {
  return <div aria-hidden style={{ borderTop: `1px solid ${CARD_DIVIDER}`, ...style }} />;
}

function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <div role="alert" style={{ marginTop: 8, fontFamily: DS2.font.body, fontSize: 14, color: DT.color.error }}>
      {children}
    </div>
  );
}

// White DS2 field (Figma "Frame 59/60") — shared look for username + country.
const WHITE_FIELD: CSSProperties = {
  background:   '#FFFFFF',
  border:       '1.5px solid #C9A84C',
  borderRadius: 24,
};

const USERNAME_MAX = 14; // authoritative backend rule (2–14) — NOT the Figma mock's 20
const USERNAME_RE  = /^[a-zA-Z0-9_]+$/;

/**
 * Save errors follow the UiMessage contract: backend-supplied prose (no
 * stable code yet) displays raw/verbatim; the app-owned fallback is stored
 * as a key so it relabels on a runtime language switch.
 */
function rawOrKey(message: string | undefined, fallbackKey: string): UiMessage {
  return message ? { kind: 'raw', text: message } : uiKey(fallbackKey);
}

/**
 * Phase 12 (Slice C3b): the same contract, now code-aware. A backend that
 * sends a stable `code` is stored as `kind: 'code'` (with the legacy prose as
 * the fallback), so the visible error relabels in place on a language switch.
 * Without a code — an older backend, or a branch that deliberately stays
 * uncoded — this is EXACTLY the previous raw/key behavior, so English output
 * and every fallback are unchanged.
 */
function codedOrRawOrKey(code: string | undefined, message: string | undefined, fallbackKey: string): UiMessage {
  if (code) return { kind: 'code', code, fallback: message || undefined };
  return rawOrKey(message, fallbackKey);
}

// ── Screen ──────────────────────────────────────────────────────────────────

export function SettingsDS2Screen() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t: tSettings } = useTranslation('settings');
  const { loggingOut, logout } = useLogout(() => navigate('/', { replace: true }));
  const confirm = useConfirm();
  const { deleting, deleteAccount } = useDeleteAccount(() => navigate('/', { replace: true }));

  // Account Deletion (App Store 5.1.1(v) / Google Play policy): destructive,
  // confirmed inside the shared ConfirmModal (confirmRun keeps it open with
  // an inline error on failure — see useDeleteAccount's own doc comment).
  const handleDeleteAccount = () => {
    void confirm({
      title:         tSettings('dangerZone.confirmTitle'),
      message:       tSettings('dangerZone.confirmMessage'),
      confirmLabel:  tSettings('dangerZone.confirmButton'),
      cancelLabel:   tSettings('dangerZone.cancelButton'),
      variant:       'danger',
      confirmVariant: 'red',
      confirmPendingLabel: tSettings('dangerZone.deleting'),
      errorText:     tSettings('dangerZone.error'),
      confirmRun:    deleteAccount,
    });
  };

  // ── Identity load (single authoritative /me fetch) ────────────────────
  const [loaded,      setLoaded]      = useState(false);
  const [displayName, setDisplayName] = useState('Player');

  // ── Username (backend, rename-capable) ────────────────────────────────
  const [usernameDraft, setUsernameDraft] = useState('');
  const confirmedUsername                 = useRef('');
  const usernameInFlight                  = useRef(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameError,  setUsernameError]  = useState<UiMessage | null>(null);
  const [usernameSaved,  setUsernameSaved]  = useState(false);

  // ── Country / Avatar (backend, confirmed-save) ────────────────────────
  const [country,       setCountry]       = useState<string | null>(null);
  const [savingCountry, setSavingCountry] = useState(false);
  const [countryError,  setCountryError]  = useState<UiMessage | null>(null);
  const [avatarKey,     setAvatarKey]     = useState<string | null>(null);
  const [savingAvatar,  setSavingAvatar]  = useState(false);
  const [avatarError,   setAvatarError]   = useState<UiMessage | null>(null);

  // ── Cosmetic account defaults (backend, confirmed-save) ───────────────
  const [tableSkin,   setTableSkin]   = useState<ViewerTableSkin | null>(null);
  const [tileSkin,    setTileSkin]    = useState<ViewerTileSkin | null>(null);
  const [savingTable, setSavingTable] = useState(false);
  const [savingTile,  setSavingTile]  = useState(false);
  const [tableError,  setTableError]  = useState<UiMessage | null>(null);
  const [tileError,   setTileError]   = useState<UiMessage | null>(null);

  // ── Device-local preferences (D2) ─────────────────────────────────────
  const [volume, setVolume] = useState(() => PreferenceSettings.getEffectiveVolumePercent());
  const [chatEnabled, setChatEnabled] = useState(() => PreferenceSettings.getTableChatEnabled());
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [hdrEnabled, setHdrEnabled] = useState(false);
  const [savingHdr,  setSavingHdr]  = useState(false);
  const [hdrError,   setHdrError]   = useState<UiMessage | null>(null);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [savingPush,  setSavingPush]  = useState(false);
  const [pushError,   setPushError]   = useState<UiMessage | null>(null);
  // One concise success/warning status line ('enabled'/'disabled'/
  // 'unregisterWarning' keys) — errors keep the ErrorLine pattern.
  const [pushNotice,  setPushNotice]  = useState<UiMessage | null>(null);
  // READ-ONLY device permission state, inspected once on load — inspecting
  // never prompts (Notification.permission / checkPermissions only).
  const [pushPermission, setPushPermission] = useState<PushPermissionState | null>(null);

  useEffect(() => {
    let alive = true;
    fetchMeShared()
      .then(me => {
        if (!alive) return;
        confirmedUsername.current = me.username ?? '';
        setUsernameDraft(me.username ?? '');
        setCountry(normalizeCountryCode(me.country_code));
        const key = normalizeAvatarKey(me.avatar_key ?? null);
        setAvatarKey(isValidAvatarKey(key) ? key : null);
        if (me.display_name) setDisplayName(me.display_name);
        setTableSkin(me.preferred_table_skin != null ? normalizeTableSkin(me.preferred_table_skin) : null);
        setTileSkin(me.preferred_tile_skin != null ? normalizeTileSkin(me.preferred_tile_skin) : null);
        setHdrEnabled(me.hdr_enabled ?? false);
        setPushEnabled(me.push_enabled ?? false);
      })
      .catch(() => { /* backend unavailable — controls stay usable with defaults */ })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  // Device push-permission state, read-only — never a prompt. Separate from
  // the /me fetch: the account preference and the device permission are
  // different facts and must not be conflated.
  useEffect(() => {
    let alive = true;
    void getPushPermissionState().then(state => { if (alive) setPushPermission(state); });
    return () => { alive = false; };
  }, []);

  // Silent registration maintenance for an already-enabled + already-granted
  // device is owned by the shell-level usePushStartupMaintenance hook (Phase
  // 11 correction) — Settings performs none of its own, so opening this
  // screen while shell maintenance is in flight cannot duplicate it.

  useEffect(() => () => { if (previewTimer.current) clearTimeout(previewTimer.current); }, []);

  // ── Username autosave (blur / Enter commit, Escape restore) ───────────
  function validateUsername(candidate: string): UiMessage | null {
    if (candidate.length < 2 || candidate.length > USERNAME_MAX) {
      return uiKey('profile.usernameLength', { max: USERNAME_MAX });
    }
    if (!USERNAME_RE.test(candidate)) {
      return uiKey('profile.usernameChars');
    }
    return null;
  }

  async function commitUsername() {
    if (!loaded || usernameInFlight.current) return; // dedupes Enter+blur double-fire
    const candidate = usernameDraft.trim();
    if (candidate === confirmedUsername.current) { setUsernameError(null); return; } // exact same value — harmless no-op, no request

    const invalid = validateUsername(candidate);
    if (invalid) { setUsernameError(invalid); return; }

    usernameInFlight.current = true;
    setSavingUsername(true);
    setUsernameError(null);
    setUsernameSaved(false);
    try {
      const result = await apiClient.updateUsername(candidate);
      if (result.ok) {
        confirmedUsername.current = result.username;
        setUsernameDraft(result.username);
        setUsernameSaved(true);
        // Push the authoritative identity to every mounted consumer now
        // (Sidebar chip, TopNav, Home) — same mechanism as avatar/country.
        void refreshIdentity();
        clearRegistrationStatusCache();
      } else if (result.error === 'username_already_set') {
        // Deploy skew: an older backend still enforces set-once. Show the
        // authoritative stored name and restore it as the confirmed value.
        confirmedUsername.current = result.username || confirmedUsername.current;
        setUsernameDraft(confirmedUsername.current);
        setUsernameError(rawOrKey(result.message, 'profile.usernameAlreadySet'));
      } else {
        // taken / invalid / unknown — the confirmed value stays authoritative;
        // the draft stays visible so the user can fix it.
        setUsernameError(codedOrRawOrKey(result.code, result.message, 'profile.usernameSaveError'));
      }
    } catch {
      setUsernameError(uiKey('profile.usernameSaveError'));
    } finally {
      usernameInFlight.current = false;
      setSavingUsername(false);
    }
  }

  function onUsernameKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); void commitUsername(); }
    if (e.key === 'Escape') {
      setUsernameDraft(confirmedUsername.current);
      setUsernameError(null);
      setUsernameSaved(false);
    }
  }

  // ── Country / avatar (same confirmed-save contract as before) ─────────
  async function handleCountrySelect(code: string | null) {
    setSavingCountry(true);
    setCountryError(null);
    try {
      const result = await apiClient.updateCountry(code);
      if (result.ok) {
        setCountry(normalizeCountryCode(result.country_code));
        void refreshIdentity();
        clearRegistrationStatusCache();
      } else {
        setCountryError(codedOrRawOrKey(result.code, result.message, 'profile.countrySaveError'));
      }
    } catch {
      setCountryError(uiKey('profile.countrySaveError'));
    } finally {
      setSavingCountry(false);
    }
  }

  async function handleAvatarSelect(key: string | null) {
    setSavingAvatar(true);
    setAvatarError(null);
    try {
      const result = await apiClient.updateAvatar(key);
      if (result.ok) {
        const saved = normalizeAvatarKey(result.avatar_key);
        setAvatarKey(isValidAvatarKey(saved) ? saved : null);
        void refreshIdentity();
        clearRegistrationStatusCache();
      } else {
        setAvatarError(codedOrRawOrKey(result.code, result.message, 'profile.avatarSaveError'));
      }
    } catch {
      setAvatarError(uiKey('profile.avatarSaveError'));
    } finally {
      setSavingAvatar(false);
    }
  }

  // ── Cosmetic account defaults (D5 — never touches a live session) ─────
  async function handleTableSkinSelect(id: string) {
    if (savingTable) return;
    setSavingTable(true);
    setTableError(null);
    try {
      const result = await apiClient.updateTableSkin(id);
      if (result.ok) {
        setTableSkin(result.preferred_table_skin != null ? normalizeTableSkin(result.preferred_table_skin) : null);
      } else {
        setTableError(codedOrRawOrKey(result.code, result.message, 'tablePieces.tableSkinSaveError'));
      }
    } catch {
      setTableError(uiKey('tablePieces.tableSkinSaveError'));
    } finally {
      setSavingTable(false);
    }
  }

  async function handleTileSkinSelect(id: string) {
    if (savingTile) return;
    setSavingTile(true);
    setTileError(null);
    try {
      const result = await apiClient.updateTileSkin(id);
      if (result.ok) {
        setTileSkin(result.preferred_tile_skin != null ? normalizeTileSkin(result.preferred_tile_skin) : null);
      } else {
        setTileError(codedOrRawOrKey(result.code, result.message, 'tablePieces.tileSkinSaveError'));
      }
    } catch {
      setTileError(uiKey('tablePieces.tileSkinSaveError'));
    } finally {
      setSavingTile(false);
    }
  }

  async function handleHdrToggle(next: boolean) {
    if (savingHdr) return;
    setSavingHdr(true);
    setHdrError(null);
    try {
      const result = await apiClient.updateHdr(next);
      if (result.ok) {
        setHdrEnabled(result.hdr_enabled);
        void refreshIdentity();
      } else {
        setHdrError(rawOrKey(result.message, 'preferences.hdrSaveError'));
      }
    } catch {
      setHdrError(uiKey('preferences.hdrSaveError'));
    } finally {
      setSavingHdr(false);
    }
  }

  // Explicit opt-in state machine (Phase 11). ENABLE order: user gesture →
  // permission prompt → device subscription/token → backend registration
  // (inside requestPushPermission) → persist push_enabled=true → identity/UI.
  // A failure at ANY stage leaves the toggle off, keeps/returns the backend
  // preference false, and rolls back a freshly created partial subscription.
  // DISABLE order: persist push_enabled=false FIRST (backend delivery stops
  // immediately) → unregister this device → UI. Unregister failure keeps the
  // account disabled and shows a non-blocking warning.
  async function handlePushToggle(next: boolean) {
    if (savingPush) return;
    setSavingPush(true);
    setPushError(null);
    setPushNotice(null);
    try {
      if (next) {
        const attempt = await requestPushPermission();
        if (!attempt.ok) {
          // 'stale' = the account that clicked the toggle is no longer the
          // active one (logout/switch mid-flight). Show NOTHING — no error,
          // no success — and mutate nothing for the new account.
          if (attempt.reason === 'stale') return;
          setPushError(uiKey(
            attempt.reason === 'unsupported' ? 'push.unsupported'
              : attempt.reason === 'blocked' ? 'push.blocked'
              : 'push.enableError',
          ));
          if (attempt.reason === 'blocked') setPushPermission('denied');
          return; // toggle stays off; backend preference untouched (false)
        }
        // Identity-scoped operation handle: the preference write, the
        // rollback, and every notice below stay bound to the INITIATING
        // account — enabling B's account (or painting B's screen) from A's
        // gesture is forbidden. The write itself goes through the guarded
        // seam, which re-verifies identity in the same tick as dispatch.
        const op = attempt.op ?? beginPushOperation();
        if (!op || !op.stillValid()) return;
        setPushPermission('granted');

        const result = await setPushEnabledPreference(true, op);
        if (result === 'stale') return;         // no write happened; present nothing
        if (!op.stillValid()) return;           // written for A, but A is gone: present nothing
        if (result.ok) {
          setPushEnabled(true);
          setPushNotice(uiKey('push.enabled'));
          void refreshIdentity();
        } else {
          // The device subscription was just created but the account could
          // not be enabled — roll the partial registration back so nothing
          // is left half-armed. Identity-bound: a stale rollback is a
          // silent no-op, never a DELETE under another account.
          void unregisterPushDevice(op);
          setPushError(rawOrKey(result.message, 'push.enableError'));
        }
        return;
      }

      // Disable: backend first, so delivery stops even if cleanup fails.
      // The whole sequence is one identity-scoped operation.
      const op = beginPushOperation();
      if (!op) return;
      const result = await setPushEnabledPreference(false, op);
      if (result === 'stale') return;           // no write happened; present nothing
      if (!result.ok) {
        if (!op.stillValid()) return;           // stale failure: no error in another account
        setPushError(rawOrKey(result.message, 'push.saveError'));
        return; // still enabled — the preference write itself failed
      }
      if (!op.stillValid()) return;             // A was disabled; B's screen shows nothing
      setPushEnabled(false);
      void refreshIdentity();
      const cleaned = await unregisterPushDevice(op);
      if (!op.stillValid()) return;             // stale cleanup: no toast in another account
      setPushNotice(uiKey(cleaned ? 'push.disabled' : 'push.unregisterWarning'));
    } catch {
      setPushError(uiKey(next ? 'push.enableError' : 'push.saveError'));
    } finally {
      setSavingPush(false);
    }
  }

  // ── Device-local preference handlers ──────────────────────────────────
  function handleVolumeChange(next: number) {
    setVolume(next);
    PreferenceSettings.setVolumePercent(next); // one write path — keeps sound_enabled in sync (D3)
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (next > 0) {
      // Live preview after the drag settles — the managers read the stored
      // volume per play, so the preview is heard at the new level.
      previewTimer.current = setTimeout(() => AppSoundManager.play('notification'), 250);
    }
  }

  function handleChatToggle(next: boolean) {
    PreferenceSettings.setTableChatEnabled(next);
    setChatEnabled(next);
  }

  // ── Render ─────────────────────────────────────────────────────────────
  const loadingText = (
    <span style={{ fontFamily: DS2.font.body, fontSize: 14, color: '#CCCCCC' }}>{tSettings('loading')}</span>
  );

  return (
    // flex column fills the shell height so Footer sticks to the bottom on
    // short pages (AppShell content-wrapper convention).
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

      {/* NO overflow property of any kind on this div — not `hidden`, not
          `overflowX` alone either. Per the CSS Overflow spec, setting only
          one axis non-visible makes the OTHER axis compute to `auto`, not
          `visible` — so a bare `overflowX:'hidden'` still turns this into a
          scroll container on the Y axis. And per the Flexbox spec, a flex
          item (this div is a flex-column child of the root above) whose
          overflow is anything other than `visible` on BOTH axes gets an
          automatic min-height of 0 instead of a content-based one — which is
          exactly what let `main`'s remaining space flex-shrink this wrapper
          and clip everything below the fold (the "Settings clipped after
          Profile, Footer rides up mid-page" regression). Leaving overflow
          fully undeclared (real `visible` on both axes, not just apparent)
          is the only way this wrapper can never become a shrinkable/scrolling
          flex item — matches every other long DS2 screen (Home/Friends/
          PlayOnline), which never wrap page content in a clipping container.
          `main` (AppShell.tsx) remains the ONE vertical scroll owner. */}
      <div data-testid="settings-page-scroll-wrapper" style={{ position: 'relative', paddingTop: isMobile ? 28 : 64, paddingBottom: isMobile ? 40 : 88 }}>
        {/* Atmospheric glows — Figma's three instances, scaled onto the DS2
            canvas. Isolated in their OWN absolutely-positioned layer (not
            direct children of the content wrapper above) so their horizontal
            containment (they're up to 1300px wide and can bleed on narrower
            desktops) can use `overflow:hidden` freely: an absolutely
            positioned box is removed from flex layout entirely — it is never
            a flex item and can never trigger the flex automatic-min-height
            trap the content wrapper above must now stay clear of. `inset:0`
            sizes this layer to the content wrapper's own padding box, so the
            glows' `top`/`leftOffset` values land pixel-identical to before. */}
        {!isMobile && (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <AtmosphericGlow width={1300} height={760} top={130} leftOffset={-250} />
            <AtmosphericGlow width={1300} height={760} top={770} leftOffset={320} />
            <AtmosphericGlow width={1300} height={760} top={1460} leftOffset={-200} />
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <PageContent maxWidth={DS2.layout.contentMaxWidth}>
            {/* Content column — Figma's 1188px cards ≈ 830px at DS2 scale. */}
            <div style={{ maxWidth: 830 }}>

              {/* ── Header row: title + subtitle, red Log Out on the right ── */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-start', justifyContent: 'space-between', gap: isMobile ? 16 : 32 }}>
                <div style={{ minWidth: 0 }}>
                  <h1
                    style={{
                      fontFamily:    DS2.font.heading,
                      fontWeight:    700,
                      fontSize:      isMobile ? 32 : `clamp(2rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
                      lineHeight:    isMobile ? '38px' : DS2.type.sectionHeading.lineHeight,
                      letterSpacing: '-0.02em',
                      color:         '#F5F0E0',
                      margin:        0,
                    }}
                  >
                    {tSettings('title')}
                  </h1>
                  <p
                    style={{
                      fontFamily: DS2.font.body,
                      fontWeight: 300,
                      fontSize:   isMobile ? 15 : `clamp(1rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
                      lineHeight: 1.6,
                      color:      DS2.color.textBright,
                      margin:     '12px 0 0',
                      maxWidth:   520,
                    }}
                  >
                    {tSettings('subtitle')}
                  </p>
                </div>

                {/* Shared useLogout — same single-flight/pending/recovery contract
                    as the Sidebar's global control; no screen-local sign-out. */}
                <button
                  type="button"
                  onClick={logout}
                  disabled={loggingOut}
                  style={{
                    flexShrink:   0,
                    alignSelf:    isMobile ? 'stretch' : 'flex-start',
                    minWidth:     isMobile ? undefined : 172,
                    padding:      '13px 28px',
                    background:   '#C20000',
                    border:       `1px solid ${DS2.color.gold}`,
                    borderRadius: DS2.radius.button,
                    color:        '#FFFFFF',
                    fontFamily:   DS2.font.body,
                    fontWeight:   500,
                    fontSize:     16,
                    textAlign:    'center',
                    cursor:       loggingOut ? 'default' : 'pointer',
                    opacity:      loggingOut ? 0.6 : 1,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {loggingOut ? tSettings('signingOut') : tSettings('logOut')}
                </button>
              </div>

              {/* ── Card 1: Profile ─────────────────────────────────────── */}
              <Card isMobile={isMobile}>
                <CardHeading title={tSettings('profile.title')} subtitle={tSettings('profile.subtitle')} />

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-start', gap: isMobile ? 24 : 56, marginTop: isMobile ? 22 : 30 }}>
                  {/* Username — controlled autosave: commit on blur/Enter, Escape restores. */}
                  <div style={{ flex: 1, minWidth: 0, maxWidth: 420 }}>
                    <FieldLabel htmlFor="settings-username">{tSettings('profile.username')}</FieldLabel>
                    <div style={{ ...WHITE_FIELD, marginTop: 12, padding: '13px 20px' }}>
                      <input
                        id="settings-username"
                        type="text"
                        value={usernameDraft}
                        maxLength={USERNAME_MAX}
                        disabled={!loaded || savingUsername}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={e => { setUsernameDraft(e.currentTarget.value); setUsernameSaved(false); }}
                        onKeyDown={onUsernameKeyDown}
                        onBlur={() => void commitUsername()}
                        style={{
                          width:      '100%',
                          border:     'none',
                          outline:    'none',
                          background: 'transparent',
                          fontFamily: DS2.font.body,
                          fontSize:   18,
                          color:      '#0B0B0B',
                          padding:    0,
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 6, gap: 12 }}>
                      <span role="status" aria-live="polite" style={{ fontFamily: DS2.font.body, fontSize: 13, color: usernameSaved ? DT.color.success : '#CCCCCC', minHeight: 16 }}>
                        {savingUsername ? tSettings('profile.saving') : usernameSaved ? tSettings('profile.saved') : ''}
                      </span>
                      <span aria-label={tSettings('profile.charCount', { n: usernameDraft.length, max: USERNAME_MAX })} style={{ fontFamily: DS2.font.body, fontSize: 14, color: '#8f8f8f' }}>
                        {usernameDraft.length}/{USERNAME_MAX}
                      </span>
                    </div>
                    {usernameError && <ErrorLine>{renderUiMessage(tSettings, usernameError)}</ErrorLine>}
                  </div>

                  {/* Avatar — the current avatar IS the trigger (circle variant of the
                      existing picker; same catalog, dropdown, and persistence). */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <FieldLabel htmlFor="avatar-picker" style={{ marginBottom: 0 }}>{tSettings('profile.avatar')}</FieldLabel>
                    {loaded
                      ? <AvatarPicker variant="circle" value={avatarKey} onSelect={handleAvatarSelect} disabled={savingAvatar} displayName={displayName} />
                      : loadingText}
                  </div>
                </div>
                {avatarError && <ErrorLine>{renderUiMessage(tSettings, avatarError)}</ErrorLine>}

                <Divider style={{ margin: isMobile ? '24px 0' : '30px 0' }} />

                {/* Country */}
                <div style={{ maxWidth: 420 }}>
                  <FieldLabel htmlFor="country-picker">{tSettings('profile.country')}</FieldLabel>
                  <div style={{ marginTop: 12 }}>
                    {loaded
                      ? (
                        <CountryPicker
                          value={country}
                          onSelect={handleCountrySelect}
                          disabled={savingCountry}
                          triggerStyle={{
                            ...WHITE_FIELD,
                            width:          '100%',
                            justifyContent: 'space-between',
                            padding:        '13px 20px',
                            color:          '#0B0B0B',
                            fontFamily:     DS2.font.body,
                            fontSize:       18,
                          }}
                        />
                      )
                      : loadingText}
                  </div>
                  {countryError && <ErrorLine>{renderUiMessage(tSettings, countryError)}</ErrorLine>}
                </div>
              </Card>

              {/* ── Card 2: Preferences ─────────────────────────────────── */}
              <Card isMobile={isMobile}>
                <CardHeading title={tSettings('preferences.title')} subtitle={tSettings('preferences.subtitle')} />

                {/* Sound volume — one control for volume AND mute (0% = muted, D3). */}
                <div style={{ marginTop: isMobile ? 22 : 30 }}>
                  <FieldLabel htmlFor="settings-volume">{tSettings('preferences.soundVolume')}</FieldLabel>
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 14 : 28, marginTop: 14 }}>
                    <p style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: 15, lineHeight: 1.35, color: DS2.color.textBright, margin: 0, maxWidth: isMobile ? undefined : 240 }}>
                      {tSettings('preferences.soundVolumeDesc')}
                    </p>
                    <VolumeSlider
                      id="settings-volume"
                      value={volume}
                      onChange={handleVolumeChange}
                      ariaLabel={tSettings('preferences.soundVolumeAria')}
                      style={{ flex: 1, minWidth: 0, maxWidth: isMobile ? undefined : 330 }}
                    />
                    <span style={{ fontFamily: DS2.font.heading, fontWeight: 700, fontSize: isMobile ? 20 : 27, letterSpacing: '0.12em', color: '#FFFFFF', minWidth: 84 }}>
                      {volume}%
                    </span>
                  </div>
                </div>

                <Divider style={{ margin: isMobile ? '24px 0' : '30px 0' }} />

                {/* Music (coming soon — honestly inert) + Table Chat */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 24 : 96 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                    <FieldLabel htmlFor="settings-music" style={{ marginBottom: 0 }}>{tSettings('preferences.music')}</FieldLabel>
                    <ToggleSwitch id="settings-music" checked={false} onChange={() => {}} disabled ariaLabel={tSettings('preferences.musicAria')} />
                    <span style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#CCCCCC' }}>
                      {tSettings('preferences.comingSoon')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                    <FieldLabel htmlFor="settings-table-chat" style={{ marginBottom: 0 }}>{tSettings('preferences.tableChat')}</FieldLabel>
                    <ToggleSwitch id="settings-table-chat" checked={chatEnabled} onChange={handleChatToggle} ariaLabel={tSettings('preferences.tableChatAria')} />
                  </div>
                </div>

                <Divider style={{ margin: isMobile ? '24px 0' : '30px 0' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                  <FieldLabel htmlFor="settings-hdr" style={{ marginBottom: 0 }}>{tSettings('preferences.hdr')}</FieldLabel>
                  <ToggleSwitch id="settings-hdr" checked={hdrEnabled} onChange={v => void handleHdrToggle(v)} disabled={!loaded || savingHdr} ariaLabel={tSettings('preferences.hdrAria')} />
                  <span style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: 13, letterSpacing: '0.06em', color: '#CCCCCC' }}>
                    {tSettings('preferences.hdrDesc')}
                  </span>
                </div>
                {hdrError && <ErrorLine>{renderUiMessage(tSettings, hdrError)}</ErrorLine>}

                <Divider style={{ margin: isMobile ? '24px 0' : '30px 0' }} />

                {/* Language — the permanent selector (device-local persistence
                    via app_language; switching is instant, no reload/logout). */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 14 : 24 }}>
                  <FieldLabel style={{ marginBottom: 0 }}>{tSettings('language.label')}</FieldLabel>
                  <LanguageSelector variant="settings" id="settings-language" />
                  <span style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: 13, letterSpacing: '0.06em', color: '#CCCCCC' }}>
                    {tSettings('language.caption')}
                  </span>
                </div>

                <Divider style={{ margin: isMobile ? '24px 0' : '30px 0' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                  <FieldLabel htmlFor="settings-push" style={{ marginBottom: 0 }}>{tSettings('push.label')}</FieldLabel>
                  <ToggleSwitch
                    id="settings-push"
                    checked={pushEnabled}
                    onChange={v => void handlePushToggle(v)}
                    disabled={!loaded || savingPush || pushPermission === 'unsupported'}
                    ariaLabel={tSettings('push.aria')}
                  />
                  <span style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: 13, letterSpacing: '0.06em', color: '#CCCCCC' }}>
                    {tSettings('push.desc')}
                  </span>
                </div>
                {/* Device-level presentation (read-only facts — no prompt):
                    unsupported browser, blocked permission, or a preference
                    that is on for the account but unusable on THIS device. */}
                {pushPermission === 'unsupported' && (
                  <div style={{ marginTop: 8, fontFamily: DS2.font.body, fontSize: 13, color: '#9A9A9A' }}>
                    {tSettings('push.unsupported')}
                  </div>
                )}
                {pushPermission === 'denied' && !pushError && (
                  <div style={{ marginTop: 8, fontFamily: DS2.font.body, fontSize: 13, color: '#9A9A9A' }}>
                    {tSettings('push.blocked')}
                  </div>
                )}
                {pushNotice && (
                  <div role="status" aria-live="polite" style={{ marginTop: 8, fontFamily: DS2.font.body, fontSize: 13, color: '#9A9A9A' }}>
                    {renderUiMessage(tSettings, pushNotice)}
                  </div>
                )}
                {pushError && <ErrorLine>{renderUiMessage(tSettings, pushError)}</ErrorLine>}
              </Card>

              {/* ── Card 3: Table & Pieces ──────────────────────────────── */}
              <Card isMobile={isMobile}>
                <CardHeading title={tSettings('tablePieces.title')} subtitle={tSettings('tablePieces.subtitle')} />

                {/* Table skin — every REAL catalog option (D6), real assets. */}
                <div style={{ marginTop: isMobile ? 22 : 30 }}>
                  <FieldLabel>{tSettings('tablePieces.tableSkin')}</FieldLabel>
                  <div style={{ marginTop: 16 }}>
                    <CosmeticCatalog
                      variant="table"
                      ariaLabel={tSettings('tablePieces.tableSkinAria')}
                      items={TABLE_SKIN_CATALOG.map(e => ({ id: e.id, label: tableSkinDisplayName(e.id, e.label), image: e.asset }))}
                      selectedId={tableSkin ?? DEFAULT_TABLE_SKIN}
                      onSelect={handleTableSkinSelect}
                      readOnly={!loaded || savingTable}
                    />
                  </div>
                  {tableError && <ErrorLine>{renderUiMessage(tSettings, tableError)}</ErrorLine>}
                </div>

                <Divider style={{ margin: isMobile ? '24px 0' : '30px 0' }} />

                {/* Tile skin — Figma labels this subsection "SKIN"; all 4 real
                    catalog options render (the mock's "Ebony White" does not exist). */}
                <div>
                  <FieldLabel>{tSettings('tablePieces.tileSkin')}</FieldLabel>
                  <div style={{ marginTop: 16 }}>
                    <CosmeticCatalog
                      variant="tile"
                      ariaLabel={tSettings('tablePieces.tileSkinAria')}
                      items={TILE_SKIN_CATALOG.map(e => ({ id: e.id, label: tileSkinDisplayName(e.id, e.label), image: e.thumbnail }))}
                      selectedId={tileSkin ?? DEFAULT_TILE_SKIN}
                      onSelect={handleTileSkinSelect}
                      readOnly={!loaded || savingTile}
                    />
                  </div>
                  {tileError && <ErrorLine>{renderUiMessage(tSettings, tileError)}</ErrorLine>}
                </div>
              </Card>

              {/* ── Card 4: Danger Zone — Account Deletion ───────────────── */}
              <Card isMobile={isMobile}>
                <CardHeading title={tSettings('dangerZone.title')} subtitle={tSettings('dangerZone.subtitle')} />

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 16, marginTop: isMobile ? 22 : 30 }}>
                  <p style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: DS2.font.body, fontWeight: 300, fontSize: 14, lineHeight: 1.6, color: '#CCCCCC' }}>
                    {tSettings('dangerZone.deleteAccountDesc')}
                  </p>
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    style={{
                      flexShrink:   0,
                      alignSelf:    isMobile ? 'stretch' : 'center',
                      minWidth:     isMobile ? undefined : 172,
                      padding:      '13px 28px',
                      background:   'transparent',
                      border:       `1px solid ${DT.color.error}`,
                      borderRadius: DS2.radius.button,
                      color:        DT.color.error,
                      fontFamily:   DS2.font.body,
                      fontWeight:   500,
                      fontSize:     16,
                      textAlign:    'center',
                      cursor:       deleting ? 'default' : 'pointer',
                      opacity:      deleting ? 0.6 : 1,
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    {deleting ? tSettings('dangerZone.deleting') : tSettings('dangerZone.deleteAccount')}
                  </button>
                </div>
              </Card>

            </div>
          </PageContent>
        </div>
      </div>

      <Footer />
    </div>
  );
}
